import { ModalProps } from "antd";
import React from "react";
import styled from "styled-components";

import { Button, Modal, SuccessCheckIcon, Typography } from "../../components";
import { COLORS } from "../../constants";

const Content = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #eaf6ed;
  padding: 10px;
  border-radius: 4px;
  width: 100%;
`;

const SuccessText = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: ${COLORS.black85};
`;

const ContentText = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const IllustrationContainer = styled.div`
  padding: 20px 0;
`;

const Illustration = styled.img`
  width: 224px;
  height: 160px;
  object-fit: contain;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  align-items: center;
`;

const StyledButton = styled(Button)`
  width: 150px;
  max-width: 220px;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.coeblue4};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 7px 12px;

  &:hover {
    text-decoration: underline;
  }
`;

type Props = ModalProps & {
  onAuthorize: () => void;
  onSkip: () => void;
  isAuthorizing?: boolean;
  /** Web dashboard renders inside an iframe; portal must use that document, not the parent window. */
  portalDocument?: Document;
};

export const SlackAuthModal: React.FC<Props> = ({
  onAuthorize,
  onSkip,
  isAuthorizing = false,
  portalDocument,
  ...modalProps
}) => {
  const containerDocument = portalDocument ?? document;
  return (
    <Modal
      {...modalProps}
      zIndex={1200}
      getContainer={() => containerDocument.body}
      title={
        <Typography fontWeight="bold" style={{ marginBottom: 0 }}>
          Enable Rich Previews in Slack?
        </Typography>
      }
      width={500}
      onCancel={onSkip}
      closable
    >
      <Content>
        <SuccessBanner>
          <SuccessCheckIcon />
          <SuccessText>Link copied to clipboard!</SuccessText>
        </SuccessBanner>

        <ContentText>
          <IllustrationContainer>
            <Illustration src="/static/slack-unfurling-preview.png" alt="Slack preview example" />
          </IllustrationContainer>

          <Typography fontWeight="bold" variant="h6">
            Let's Connect to Slack
          </Typography>

          <Typography color="textSecondary" style={{ lineHeight: 1.25, fontSize: 16 }}>
            Connecting to Slack gives your team instant rich previews, with dashboard screenshots
            and charts, when links are shared.
          </Typography>
        </ContentText>

        <ButtonContainer>
          <StyledButton
            type="primary"
            onClick={onAuthorize}
            disabled={isAuthorizing}
            loading={isAuthorizing}
          >
            Connect Slack
          </StyledButton>

          <SkipButton onClick={onSkip}>Skip for now</SkipButton>
        </ButtonContainer>
      </Content>
    </Modal>
  );
};
